import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { inboxNeedsAttentionCount } from '@/services/botsApi';
import type { DesignRoleId } from '@/config/designRoles';

/**
 * THE SECOND LINE UNDER A RAIL DOOR.
 *
 * Reception's canvas puts one under every door — "now #7 · 5 waiting · 2 not
 * arrived" under Queue, "3 need a human · 4 uploads · 9 to call" under
 * Messages centre. It is §13.3's rule that the sub is a COUNT or a contents
 * list, never a description: the rail tells the desk what is waiting before
 * they open anything.
 *
 * The contents lists are static and live in `designRoles.ts` beside the door.
 * The counts are here, because they are data.
 *
 * WHY IN THE SHELL AND NOT IN THE ROLE'S FOLDER. The rail is drawn once for
 * every surface, on every route; a hook that lives in one role's module could
 * only be called conditionally, which React forbids. So the shell asks the
 * same questions every time and the queries answer only for the surface that
 * has them — `enabled` is how a surface opts in, not an `if`.
 */

const n = (v: number) => v.toLocaleString('en-US');
const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

interface DeskCounts {
  counts?: { now?: number | null; waiting?: number; notArrived?: number };
  uploadsToFile?: number;
  toCall?: number;
}

export function useRailSubs(surface: DesignRoleId): Record<string, string | undefined> {
  const isDesk = surface === 'reception';

  /* The same key the desk's own screens use, so the rail reads their cache
     rather than asking again. */
  const day = useQuery({
    queryKey: ['reception-desk', 'day', null],
    queryFn: async () => unwrap<DeskCounts>((await apiClient.get('/reception-desk/day')).data),
    enabled: isDesk,
    staleTime: 10_000,
    retry: false,
  });

  /* The desk's own helper and the desk's own key, so this shares their cache
     rather than asking a second time. */
  const needsHuman = useQuery({
    queryKey: ['reception-desk', 'needs-human'],
    queryFn: () => inboxNeedsAttentionCount(),
    enabled: isDesk,
    staleTime: 60_000,
    retry: false,
  });

  const patients = useQuery({
    queryKey: ['rail-sub', 'patients-total'],
    queryFn: async () => {
      /* THE DESK'S OWN FILE COUNT — the Patients page prints this same number
         ("files"), and `/patients` counts the demo and provisional files the
         desk never lists, so the rail said 159 over a page that said 158. */
      const res = await apiClient.get('/reception-desk/patients', { params: { limit: 1 } });
      const body = (res.data as { data?: { files?: number } })?.data ?? (res.data as { files?: number });
      return body?.files ?? null;
    },
    enabled: isDesk,
    staleTime: 300_000,
    retry: false,
  });

  if (!isDesk) return {};

  const c = day.data?.counts;
  /* A count that has not arrived says nothing rather than printing a zero it
     does not know — the same rule the desk's own cards follow. */
  const queue = c
    ? [
        c.now != null ? `now #${c.now}` : null,
        c.waiting != null ? `${c.waiting} waiting` : null,
        c.notArrived ? `${c.notArrived} not arrived` : null,
      ].filter(Boolean).join(' · ')
    : undefined;

  const human = needsHuman.data;
  const messages = [
    human ? `${human} need a human` : null,
    day.data?.uploadsToFile ? `${day.data.uploadsToFile} uploads` : null,
    day.data?.toCall ? `${day.data.toCall} to call` : null,
  ].filter(Boolean).join(' · ');

  return {
    queue: queue || undefined,
    patients: patients.data != null ? n(patients.data) : undefined,
    messages: messages || undefined,
  };
}

/**
 * The number pinned to the right of a door — Messages centre on the desk's
 * canvas wears the total of what is waiting behind it, so the desk can see
 * there is work without reading the three counts. Derived from the sub the
 * rail already has, so it costs no second question.
 */
export function railBadges(
  surface: DesignRoleId,
  subs: Record<string, string | undefined>,
): Record<string, string | undefined> {
  if (surface !== 'reception') return {};
  const waiting = (subs.messages ?? '')
    .split(' · ')
    .map((part) => Number(part.split(' ')[0]))
    .filter((v) => Number.isFinite(v))
    .reduce((a, b) => a + b, 0);
  return { messages: waiting ? String(waiting) : undefined };
}
